import { useState, useEffect, useCallback } from 'react';
import { Search, User, CheckCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { userService } from '@/services';

export interface Student {
  id: string;
  nombre: string;
  apellido: string;
  email: string;
  rut: string;
  activo: boolean;
}

interface StudentSelectorProps {
  onStudentSelect: (student: Student) => void;
  selectedStudent: Student | null;
  disabled?: boolean;
  className?: string;
}

export function StudentSelector({
  onStudentSelect,
  selectedStudent,
  disabled = false,
  className,
}: StudentSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [students, setStudents] = useState<Student[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce(async (term: string) => {
      if (!term || term.length < 2) {
        setStudents([]);
        return;
      }

      setIsSearching(true);
      setError(null);

      try {
        const response = await userService.searchStudentsByName(term);
        setStudents(response);
      } catch (err) {
        setError('Error al buscar estudiantes');
        setStudents([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  const handleStudentSelect = (student: Student) => {
    onStudentSelect(student);
    setSearchTerm('');
    setStudents([]);
  };

  const handleClearSelection = () => {
    onStudentSelect(null as any);
    setSearchTerm('');
    setStudents([]);
  };

  return (
    <div className={cn('space-y-4', className)}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5" />
            Seleccionar Estudiante
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Estudiante seleccionado */}
          {selectedStudent && (
            <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900 dark:text-green-100">
                      {selectedStudent.nombre} {selectedStudent.apellido}
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      RUT: {selectedStudent.rut}
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      {selectedStudent.email}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  disabled={disabled}
                  className="text-green-600 hover:text-green-700 hover:bg-green-100 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-950/40"
                >
                  Cambiar
                </Button>
              </div>
            </div>
          )}

          {/* Búsqueda de estudiantes */}
          {!selectedStudent && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar estudiante por nombre o RUT..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={disabled}
                  className="pl-10"
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>

              {/* Error */}
              {error && (
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              )}

              {/* Resultados de búsqueda */}
              {students.length > 0 && (
                <Card>
                  <CardContent className="p-0">
                    <ScrollArea className="h-48">
                      <div className="space-y-1 p-2">
                        {students.map((student) => (
                          <Button
                            key={student.id}
                            variant="ghost"
                            className="w-full justify-start h-auto p-3 hover:bg-muted/50"
                            onClick={() => handleStudentSelect(student)}
                            disabled={disabled}
                          >
                            <div className="flex items-center gap-3 text-left">
                              <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">
                                  {student.nombre} {student.apellido}
                                </p>
                                <p className="text-sm text-muted-foreground truncate">
                                  RUT: {student.rut}
                                </p>
                                <p className="text-sm text-muted-foreground truncate">
                                  {student.email}
                                </p>
                              </div>
                              <Badge
                                variant={student.activo ? 'default' : 'secondary'}
                                className="flex-shrink-0"
                              >
                                {student.activo ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </div>
                          </Button>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {/* Mensaje cuando no hay resultados */}
              {searchTerm.length >= 2 && !isSearching && students.length === 0 && !error && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No se encontraron estudiantes con ese término de búsqueda
                </p>
              )}

              {/* Instrucciones */}
              {!searchTerm && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  Ingrese al menos 2 caracteres para buscar estudiantes
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Función debounce helper
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}
